import firebase from "firebase/app";
import "firebase/firestore";

import firestore from "src/apiService/firestore";
import type {
  VintedIntegration,
  VintedInventoryImportResume,
  VintedSalesImportResume,
} from "src/interfaces/plaidIntegration.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import { eachMonthInclusive } from "src/utils/monthYear";
import type { SyncLock } from "../syncLock";
import VintedClient from "./VintedClient";
import {
  addVintedThreadIdsToSet,
  buildThreadIdAliasMap,
  getInventoryListingId,
  getMrgSaleTransactionId,
  uploadInventoryBatch,
  uploadSalesBatch,
} from "./vintedBatches";
import {
  analyzeCheckpointPageSync,
  analyzeForwardPageSync,
  inventorySyncedFlagsForPage,
  salesSyncedFlagsForPage,
} from "./resumeCutPoint";

export async function saveVintedSalesResume(
  integrationId: string,
  resume: VintedSalesImportResume
) {
  await firestore().collection("Plaid_Integrations").doc(integrationId).update({
    vintedSalesImportResume: resume,
  });
}

export async function clearVintedSalesResume(integrationId: string) {
  await firestore().collection("Plaid_Integrations").doc(integrationId).update({
    vintedSalesImportResume: firebase.firestore.FieldValue.delete(),
  });
}

export async function saveVintedInventoryResume(
  integrationId: string,
  resume: VintedInventoryImportResume
) {
  await firestore().collection("Plaid_Integrations").doc(integrationId).update({
    vintedInventoryImportResume: resume,
  });
}

export async function clearVintedInventoryResume(integrationId: string) {
  await firestore().collection("Plaid_Integrations").doc(integrationId).update({
    vintedInventoryImportResume: firebase.firestore.FieldValue.delete(),
  });
}

function getOrderDate(order: Record<string, unknown>): Date | null {
  const raw = order.date;
  if (!raw) return null;
  const d = new Date(String(raw));
  return Number.isNaN(d.getTime()) ? null : d;
}

function filterOrdersForDateRange(
  orders: unknown[],
  startDate: Date,
  endDate: Date,
  seenOrderIds: Set<string>
): { filtered: unknown[]; shouldStop: boolean } {
  let shouldStop = false;
  const filtered = orders.filter((order) => {
    const transactionId = getMrgSaleTransactionId(order);
    if (transactionId) {
      if (seenOrderIds.has(transactionId)) return false;
      seenOrderIds.add(transactionId);
    }
    const orderDate = getOrderDate(order as Record<string, unknown>);
    if (!orderDate) return true;
    if (orderDate < startDate) {
      shouldStop = true;
      return false;
    }
    return orderDate >= startDate && orderDate <= endDate;
  });
  return { filtered, shouldStop };
}

/**
 * List-only walk of `my_orders` for the import date window (no enrich).
 * Used before the wallet phase when this process did not already accumulate
 * the full sales id set (e.g. resume into transactions, or resume mid-orders).
 *
 * One API page pass: for each order in range, add its MRG `transaction_id`.
 */
export async function collectMrgSaleTransactionIdsForDateRange(
  client: VintedClient,
  startDate: Date,
  endDate: Date,
  lock: SyncLock
): Promise<Set<string>> {
  const pageDedupIds = new Set<string>();
  const knownSaleTransactionIds = new Set<string>();
  for (let page = 1; ; page++) {
    lock.throwIfAborted();
    const res = await client.getMyOrders({ page });
    const orders: unknown[] = res?.my_orders || [];
    const totalPages = res?.pagination?.total_pages || 1;
    let shouldStop = false;

    for (const order of orders) {
      const id = getMrgSaleTransactionId(order);
      if (id) {
        if (pageDedupIds.has(id)) continue;
        pageDedupIds.add(id);
      }

      const orderDate = getOrderDate(order as Record<string, unknown>);
      if (orderDate && orderDate < startDate) {
        shouldStop = true;
        continue;
      }
      if (
        id &&
        (!orderDate || (orderDate >= startDate && orderDate <= endDate))
      ) {
        addVintedThreadIdsToSet(knownSaleTransactionIds, order);
      }
    }

    if (shouldStop || page >= totalPages) break;
  }
  return knownSaleTransactionIds;
}

type EnrichOrders = (
  client: VintedClient,
  orders: Record<string, unknown>[]
) => Promise<Record<string, unknown>[]>;

type EnrichInventory = (
  client: VintedClient,
  items: Record<string, unknown>[],
  page: number
) => Promise<Record<string, unknown>[]>;

/**
 * Find where to resume orders: returns starting API page and optional slice
 * start index within that page.
 */
export async function findOrdersResumeCursor(
  client: VintedClient,
  resume: VintedSalesImportResume,
  startDate: Date,
  endDate: Date
): Promise<{ page: number; startIndex: number; done: boolean }> {
  let page = Math.max(1, resume.page || 1);
  let firstPage = true;

  for (;;) {
    const res = await client.getMyOrders({ page });
    const orders: unknown[] = res?.my_orders || [];
    const totalPages = res?.pagination?.total_pages || 1;
    const seen = new Set<string>();
    const { filtered, shouldStop } = filterOrdersForDateRange(
      orders,
      startDate,
      endDate,
      seen
    );

    if (!filtered.length) {
      if (shouldStop || page >= totalPages) {
        return { page, startIndex: 0, done: true };
      }
      page++;
      firstPage = false;
      continue;
    }

    const flags = await salesSyncedFlagsForPage(
      filtered,
      resume.uploadSalesId,
      resume.uploadReturnsId,
      undefined,
      async (threadId) => {
        const res = await client.getConversation(threadId);
        const id = (res as { conversation?: { id?: unknown } })?.conversation
          ?.id;
        return id == null || id === "" ? null : String(id);
      }
    );
    const analysis = firstPage
      ? analyzeCheckpointPageSync(flags)
      : analyzeForwardPageSync(flags);

    if (analysis.kind === "page-complete" || analysis.kind === "empty") {
      if (shouldStop || page >= totalPages) {
        return { page, startIndex: 0, done: true };
      }
      page++;
      firstPage = false;
      continue;
    }

    return { page, startIndex: analysis.startIndex, done: false };
  }
}

export async function findInventoryResumeCursor(
  client: VintedClient,
  userId: string,
  resume: VintedInventoryImportResume
): Promise<{ page: number; startIndex: number; done: boolean }> {
  let page = Math.max(1, resume.page || 1);
  let firstPage = true;

  for (;;) {
    const res = await client.getWardrobeItems({ userId, page });
    const items: unknown[] = res?.items || [];
    const totalPages = res?.pagination?.total_pages || 1;

    if (!items.length) {
      if (page >= totalPages) return { page, startIndex: 0, done: true };
      page++;
      firstPage = false;
      continue;
    }

    const flags = await inventorySyncedFlagsForPage(
      items,
      resume.uploadInventoryId
    );
    const analysis = firstPage
      ? analyzeCheckpointPageSync(flags)
      : analyzeForwardPageSync(flags);

    if (analysis.kind === "page-complete" || analysis.kind === "empty") {
      if (page >= totalPages) return { page, startIndex: 0, done: true };
      page++;
      firstPage = false;
      continue;
    }

    return { page, startIndex: analysis.startIndex, done: false };
  }
}

/** Last fully handed-off wallet month → index of the next month to process. */
export function walletResumeStartMonthIndex(
  months: MonthYear[],
  lastCompletedMonth?: MonthYear
): number {
  if (!lastCompletedMonth) return 0;
  const idx = months.findIndex(
    (m) =>
      m.year === lastCompletedMonth.year && m.month === lastCompletedMonth.month
  );
  if (idx < 0) return 0;
  return idx + 1;
}

export async function runOrdersImportPages(args: {
  client: VintedClient;
  integration: VintedIntegration;
  startDate: Date;
  endDate: Date;
  start: MonthYear;
  end: MonthYear;
  importType: "initial-import" | "direct-import";
  timestamp: number;
  uploadSalesId: string;
  uploadReturnsId: string;
  batchIndex: number;
  startPage: number;
  startIndex: number;
  lock: SyncLock;
  enrichOrders: EnrichOrders;
}): Promise<{
  batchIndex: number;
  /** MRG sale transaction_ids (all thread shapes) for orders in range this run. */
  knownSaleTransactionIds: Set<string>;
  /** Any thread id → all aliases seen on enriched orders this run. */
  threadIdByAnyId: Map<string, string[]>;
}> {
  const {
    client,
    integration,
    startDate,
    endDate,
    start,
    end,
    importType,
    timestamp,
    uploadSalesId,
    uploadReturnsId,
    lock,
    enrichOrders,
  } = args;
  let batchIndex = args.batchIndex;
  let page = args.startPage;
  let startIndex = args.startIndex;
  const seenOrderIds = new Set<string>();
  const knownSaleTransactionIds = new Set<string>();
  const threadIdByAnyId = new Map<string, string[]>();

  for (;;) {
    lock.throwIfAborted();
    const res = await client.getMyOrders({ page });
    const orders: unknown[] = res?.my_orders || [];
    const totalPages = res?.pagination?.total_pages || 1;
    const { filtered, shouldStop } = filterOrdersForDateRange(
      orders,
      startDate,
      endDate,
      seenOrderIds
    );

    // List-row ids (pre-enrich) so resume mid-page still covers the full set.
    for (const order of filtered) {
      addVintedThreadIdsToSet(knownSaleTransactionIds, order);
    }

    const slice = startIndex > 0 ? filtered.slice(startIndex) : filtered;
    startIndex = 0;

    const detailedOrders = await enrichOrders(
      client,
      slice as Record<string, unknown>[]
    );

    for (const order of detailedOrders) {
      addVintedThreadIdsToSet(knownSaleTransactionIds, order);
    }
    for (const [id, aliases] of buildThreadIdAliasMap(detailedOrders)) {
      const existing = threadIdByAnyId.get(id) || [];
      for (const alias of aliases) {
        if (!existing.includes(alias)) existing.push(alias);
      }
      threadIdByAnyId.set(id, existing);
    }

    await uploadSalesBatch(
      integration,
      detailedOrders,
      importType,
      {
        uploadSalesId,
        uploadReturnsId,
        timestamp,
        batchIndex: batchIndex++,
      },
      lock
    );

    await saveVintedSalesResume(integration.id, {
      phase: "orders",
      page,
      batchIndex,
      timestamp,
      importType,
      uploadSalesId,
      uploadReturnsId,
      start,
      end,
    });

    if (shouldStop || page >= totalPages) break;
    page++;
  }

  return { batchIndex, knownSaleTransactionIds, threadIdByAnyId };
}

export async function runInventoryImportPages(args: {
  client: VintedClient;
  integration: VintedIntegration;
  importType: "initial-import" | "direct-import";
  timestamp: number;
  uploadInventoryId: string;
  batchIndex: number;
  startPage: number;
  startIndex: number;
  lock: SyncLock;
  enrichInventory: EnrichInventory;
}): Promise<void> {
  const {
    client,
    integration,
    importType,
    timestamp,
    uploadInventoryId,
    lock,
    enrichInventory,
  } = args;
  let batchIndex = args.batchIndex;
  let page = args.startPage;
  let startIndex = args.startIndex;
  const seenListingIds = new Set<string>();

  for (;;) {
    lock.throwIfAborted();
    const res = await client.getWardrobeItems({
      userId: integration.uid,
      page,
    });
    const items: unknown[] = res?.items || [];
    const totalPages = res?.pagination?.total_pages || 1;

    const pageItems = items.filter((item) => {
      const listingId = getInventoryListingId(item);
      if (!listingId) return true;
      if (seenListingIds.has(listingId)) return false;
      seenListingIds.add(listingId);
      return true;
    });

    const slice = startIndex > 0 ? pageItems.slice(startIndex) : pageItems;
    startIndex = 0;

    if (slice.length) {
      const detailedItems = await enrichInventory(
        client,
        slice as Record<string, unknown>[],
        page
      );
      await uploadInventoryBatch(
        integration,
        detailedItems,
        importType,
        {
          uploadInventoryId,
          timestamp,
          batchIndex: batchIndex++,
        },
        lock
      );
    }

    await saveVintedInventoryResume(integration.id, {
      page,
      batchIndex,
      timestamp,
      importType,
      uploadInventoryId,
    });

    if (page >= totalPages) break;
    page++;
  }
}

export function monthsForSalesRange(
  start: MonthYear,
  end: MonthYear
): MonthYear[] {
  return [...eachMonthInclusive(start, end)] as MonthYear[];
}
